import { useCallback, useState } from 'react'
import { message } from 'antd'
import CrawlerRequest from '../requests/CrawlerRequest'
import authenticationStore from '../stores/authenticationStore'

const useImsatSync = ({ defaultUsername = '', onSyncComplete } = {}) => {
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncModalVisible, setSyncModalVisible] = useState(false)
  const [syncUsername, setSyncUsername] = useState('')
  const [syncPassword, setSyncPassword] = useState('')
  const [rememberPassword, setRememberPassword] = useState(true)
  const [hasSavedPassword, setHasSavedPassword] = useState(false)
  const [syncError, setSyncError] = useState('')

  const openSyncModal = useCallback(async (overrideUsername) => {
    const uname = (
      (typeof overrideUsername === 'string' && overrideUsername) ||
      defaultUsername ||
      authenticationStore.currentUser?.username ||
      ''
    ).trim()

    setSyncUsername(uname)
    setSyncError('')
    setRememberPassword(true)
    setSyncModalVisible(true)

    try {
      const resp = await CrawlerRequest.getCredentials()
      const credList = Array.isArray(resp.data?.data)
        ? resp.data.data
        : (Array.isArray(resp.data) ? resp.data : [])

      let userCred = null
      if (uname) {
        userCred = credList.find(
          c =>
            c.username?.trim()?.toLowerCase() === uname.toLowerCase() &&
            (String(c.is_active) === '1' || c.is_active === true)
        )
      } else if (credList.length > 0) {
        userCred = credList.find(
          c => String(c.is_active) === '1' || c.is_active === true
        ) || credList[0]
        if (userCred?.username) {
          setSyncUsername(userCred.username)
        }
      }

      if (userCred) {
        setHasSavedPassword(true)
        setSyncPassword(userCred.password || '')
        setRememberPassword(true)
      } else {
        setHasSavedPassword(false)
        setSyncPassword('')
      }
    } catch (err) {
      console.error('Failed to fetch crawler credentials status:', err)
      setHasSavedPassword(false)
      setSyncPassword('')
    }
  }, [defaultUsername])

  const closeSyncModal = useCallback(() => {
    setSyncModalVisible(false)
    setSyncPassword('')
    setSyncError('')
  }, [])

  const handleSync = useCallback(async () => {
    setSyncError('')

    const username = (syncUsername || '').trim()
    const password = syncPassword || ''

    if (!username) {
      setSyncError('Vui lòng nhập tên đăng nhập !')
      return
    }
    if (!password && !hasSavedPassword) {
      setSyncError('Vui lòng nhập mật khẩu tài khoản !')
      return
    }

    setIsSyncing(true)
    try {
      const verifyResp = await CrawlerRequest.verifyCredentials(
        username,
        password,
        rememberPassword
      )

      if (!verifyResp.data?.success) {
        setSyncError(
          verifyResp.data?.error ||
            'Mật khẩu đồng bộ không chính xác hoặc hết hạn.'
        )
        setIsSyncing(false)
        return
      }

      closeSyncModal()

      message.info('Đang đồng bộ dữ liệu, vui lòng đợi trong giây lát...')

      await CrawlerRequest.startCrawl({
        username,
        phase: 'full',
        wait: true,
        skip_user_sync: true,
      })

      message.success('Đồng bộ dữ liệu thành công!')

      if (onSyncComplete) {
        await onSyncComplete()
      }
    } catch (err) {
      message.error('Không thể kết nối đến máy chủ đồng bộ dữ liệu!')
      console.error(err)
    } finally {
      setIsSyncing(false)
    }
  }, [
    closeSyncModal,
    hasSavedPassword,
    onSyncComplete,
    rememberPassword,
    syncPassword,
    syncUsername,
  ])

  return {
    isSyncing,
    syncModalVisible,
    syncUsername,
    setSyncUsername,
    syncPassword,
    setSyncPassword,
    rememberPassword,
    setRememberPassword,
    hasSavedPassword,
    syncError,
    openSyncModal,
    closeSyncModal,
    handleSync,
  }
}

export default useImsatSync
